import type * as React from 'react';
import { CLIENT_CAPABILITIES } from './clientCapabilities';

/** Client pages behind client:financial_docs (F6 B), whatever their group. */
const CLIENT_FINANCIAL_PATHS: ReadonlySet<string> = new Set([
  "/billing/invoices",
  "/billing/ledger",
  "/dashboard/financials",
]);

// Navigation interface
interface NavLink {
  label: string;
  path: string;
  permissions: string[];
  priority?: number;
  icon?: React.ComponentType;
  group?: string;
  description?: string;
  adminCategory?: "users" | "content" | "logistics" | "compliance" | "finance" | "system"; // For admin menu grouping
}

// Centralized navigation labels to avoid drift between UI, tests, and help content
export const NAV_LABELS = {
  FINANCIAL_DASHBOARD: "Financials",
  CREDITS: "Credits",
} as const;

// Import Material-UI icons (will be used in components)
// Streamlined to only include working navigation items
export const navigationIcons = {
  Dashboard: "DashboardIcon",
  "My Jobs": "WorkIcon", // Renamed from "Jobs Dashboard" for client clarity
  Analytics: "AssessmentIcon",
  "Compliance": "FolderIcon",
  "Documents": "FolderIcon",
  "Allocated Compliance": "FolderIcon",
  "Admin Dashboard": "AdminPanelSettingsIcon",
  "User Management": "SupervisorAccountIcon",
  "Document Review": "RateReviewIcon",
  "Document Requirements": "FolderIcon",
  "Document Types": "DescriptionIcon",
  "NSW Licence Lookup (Test)": "VerifiedUserIcon",
  "Financial Dashboard": "AccountBalanceIcon",
  "Job Projections": "TrendingUpIcon",
  "Cash Flow Forecast": "TrendingUpIcon",
  "Verification Dashboard": "VerifiedUserIcon",
  "Rejection Templates": "ErrorIcon",
  "Pricing Configuration": "AttachMoneyIcon",
  "Communication Templates": "EmailIcon",
  "Campaigns": "CampaignIcon",
  "Communication Logs": "NotificationsIcon",
  "Remittance History": "ReceiptLongIcon",

  "System Health": "MonitorHeartIcon",
  "Scheduling SLA Hub": "AccessTimeIcon",
  "Audit Logs": "HistoryIcon",
  "Customer Approvals": "HowToRegIcon",
  "Workflow Approvals": "RateReviewIcon",
  "Inbox": "EmailIcon",
  "Team": "GroupIcon",
  "Settings": "SettingsIcon",
  "Job Settings": "SettingsIcon",
  "Trade Types": "CategoryIcon",
  "Certificate Rules": "VerifiedUserIcon",
  "Service Types": "CategoryIcon",
  "Disputes": "GavelIcon",
  "Workflows": "AccountTreeIcon",
  "Workflow Templates": "AccountTreeIcon",
  "Integration Templates": "SyncIcon",
  "Variation Detection Prompts": "TuneIcon",
  "Platform Staff": "SupervisorAccountIcon",
  Calendar: "CalendarMonthIcon",
  Reviews: "StarIcon",
  "Platform Documents": "DescriptionIcon",
  Quotes: "DescriptionIcon",
  Payments: "PaymentsIcon",
  "My Rates": "PriceCheckIcon", // Renamed from "Service Rates" for trade clarity
  "Financials": "AccountBalanceIcon", // Renamed from "Financials" for client clarity
  "Variations": "BuildIcon", // Renamed back from "Scope Changes" for client clarity
  "Credits": "ReceiptLongIcon", // Renamed from "Client Ledger" for clarity
  "Variation Disputes": "GavelIcon",
  "Cancellation Penalties": "MoneyOffIcon",
  "My Variations": "BuildIcon",
  "Invoices": "ReceiptIcon",
  "Job Pipeline": "ViewKanbanIcon",
  "Delivery Pipeline": "ViewKanbanIcon",
  "My Reviews": "StarIcon",
  "Follow-ups": "TaskAltIcon",
  "Quote Templates": "DescriptionIcon",
  "Staff Performance": "AssessmentIcon",
  "Service Area": "MapIcon",
  "Staff Schedule": "CalendarMonthIcon",
  "Live Job Monitor": "MonitorHeartIcon",
  "Job Matching": "TaskAltIcon",
  "Help Center": "HelpOutlineIcon",
  "Expert Manufacturers": "BusinessIcon",
  "Expert Knowledge": "SmartToyIcon",
  "Technical Expert": "SmartToyIcon",
  "Expert Transcripts": "RateReviewIcon",
  "AI Conversations": "SmartToyIcon",
  "Expert Metrics": "BarChartIcon",
  "Expert Pilot": "TuneIcon",
  "Partner QR": "QrCodeIcon",
  "Expert Test Sets": "TaskAltIcon",
  "Expert Flows": "AccountTreeIcon",
  "Field Tips": "RateReviewIcon",
  "AI Analyst": "SmartToyIcon",
  "Accreditations": "BuildIcon",
  "Specializations": "CategoryIcon",
  "Notification Log": "NotificationsIcon",
  "Trade Analytics": "BarChartIcon",
  "App Rollout": "BarChartIcon",
  "Coverage Map": "MapIcon",
  "Industry News": "NewspaperIcon",

  "QR Codes": "QrCodeIcon",
  "Referrals & Payouts": "QrCodeIcon",

  "Issue Center": "BugReportIcon",
  "Roles & Permissions": "SecurityIcon",
  "Balance Top-Up": "AddCardIcon",

  // Direct-to-Customer feature
  "My Customers": "PeopleIcon",
  "Direct Quotes": "RequestQuoteIcon",
  "Direct Jobs": "WorkOutlineIcon",
  "Branding": "PaletteIcon",
  "Direct Job Settings": "TuneIcon",
  "Integrations": "SyncIcon",
  "API & Webhooks": "SyncIcon",
  "Warranties": "SecurityIcon",
  "Company Compliance": "BusinessIcon",
  "All Variations": "BuildIcon",
  "SMS Conversations": "SmsIcon",
  "Warehouses & Locations": "LocationOnIcon",
  "Products & Stock": "CategoryIcon",
  "Warehouse Staging": "ReceiptLongIcon",
  "Fleet / Vehicles": "LocalShippingIcon",
  "Fleet": "BuildIcon",
  "Registrations": "HowToRegIcon",
  "Cases": "BuildIcon",
  "Approvals": "TaskAltIcon",
  "Catalogue": "PriceCheckIcon",
  "Network": "GroupIcon",
  "Service Cases": "BuildIcon",
  "My Vehicles": "LocalShippingIcon",
  "CRM": "ViewKanbanIcon",
} as const;

// Streamlined navigation structure - only working routes
// Removed all non-existent pages to prevent broken links
export const navigationItems: NavLink[] = [
  // Core Dashboard - entry point for all users
  {
    label: "Dashboard",
    path: "/dashboard",
    permissions: ["user:view", "job:view", "quote:view"],
    priority: 100,
    group: "main",
    description: "Overview of your activities and key metrics",
  },
  {
    label: "Inbox",
    path: "/dashboard/messages",
    permissions: ["user:view", "job:view", "quote:view"],
    priority: 95,
    group: "main",
    description: "Messaging inbox",
  },
  {
    label: "Help Center",
    path: "/library",
    permissions: ["user:view"],
    priority: 90,
    group: "main",
    description: "Browse guides, FAQs, and help documentation",
  },

  {
    label: "Calendar",
    path: "/dashboard/calendar",
    permissions: ["job:view"],
    priority: 95,
    group: "jobs", // Trade: active job management
    description: "View and sync your scheduled jobs",
  },
  {
    label: "Staff Schedule",
    path: "/dashboard/staff-schedule",
    permissions: ["job:edit"],
    priority: 87,
    group: "jobs", // Trade: team job assignments
    description: "View job assignments across your team",
  },
  {
    label: "Staff Performance",
    path: "/dashboard/staff-performance",
    permissions: ["user:edit"], // change from staff:view
    priority: 86.5,
    group: "jobs", // Trade: team performance metrics
    description: "View staff performance metrics and analytics",
  },
  {
    label: "Job Pipeline",
    path: "/dashboard/lead-pipeline",
    permissions: ["job:view"],
    priority: 86,
    group: "jobs", // Moved from leads to jobs
    description: "Manage job leads and quotes",
  },
  {
    label: "Delivery Pipeline",
    path: "/dashboard/delivery-pipeline",
    permissions: ["job:view"],
    priority: 86,
    group: "jobs", // Client: tracking their posted jobs
    description: "Track your jobs through each stage",
  },
  {
    label: "Workflows",
    path: "/settings/workflows",
    permissions: ["company:edit"],
    priority: 76.5,
    group: "settings",
    description: "Manage workflow templates for your jobs",
  },
  {
    label: "My Reviews",
    path: "/dashboard/my-reviews",
    permissions: ["job:view"],
    priority: 85,
    group: "jobs",
    description: "View and respond to client reviews",
  },
  {
    label: "Follow-ups",
    path: "/dashboard/follow-ups",
    permissions: ["job:view"],
    priority: 84.5,
    group: "jobs",
    description: "Post-completion workflow steps across your completed jobs",
  },
  {
    label: "Workflow Approvals",
    path: "/dashboard/workflow-approvals",
    permissions: ["job:view"],
    priority: 84.2,
    group: "jobs",
    description: "Review and action pending workflow approval gates",
  },
  {
    label: "Warranties",
    path: "/dashboard/warranty",
    permissions: ["job:view"],
    priority: 84,
    group: "jobs",
    description: "Track warranty claims and callbacks on completed jobs",
  },
  // Job Management - restructured into groups
  {
    label: "My Jobs", // Renamed from "Jobs Dashboard" for clarity
    path: "/dashboard/jobs",
    permissions: ["job:view"],
    priority: 94,
    group: "jobs", // Trade: active job management, Client: their jobs
    description: "View and manage your jobs",
  },
  {
    label: "Analytics",
    path: "/dashboard/analytics",
    permissions: ["analytics:view"],
    priority: 85,
    group: "jobs", // Trade performance
    description: "Job performance metrics and insights",
  },
  {
    label: "Quotes",
    path: "/dashboard/quotes",
    permissions: ["quote:view"],
    priority: 84,
    group: "leads", // Trade: finding new work
    description: "View and manage quotes for jobs",
  },
  {
    label: "Quote Templates",
    path: "/dashboard/quote-templates",
    permissions: ["quote:create"],
    priority: 83.5,
    group: "leads", // Trade: quote templates for faster quoting
    description: "Manage reusable quote templates",
  },
  {
    label: "Payments",
    path: "/dashboard/payments",
    permissions: ["company:edit"],
    priority: 82,
    group: "financials", // Trade: earnings and payouts
    description: "Manage payouts and financial dashboard",
  },
  {
    label: "My Rates", // Renamed from "Service Rates" for trade clarity
    path: "/settings/rates",
    permissions: ["job:view"],
    priority: 81,
    group: "settings",
    description: "Manage your trade service rates and call-out fees",
  },
  {
    label: "Service Area",
    path: "/dashboard/service-area",
    permissions: ["company:edit"],
    priority: 80.8,
    group: "leads", // Trade: service area configuration
    description: "Configure your service coverage area on the map",
  },
  {
    label: NAV_LABELS.FINANCIAL_DASHBOARD,
    path: "/dashboard/financials",
    permissions: ["financial:view", "company:edit"],
    priority: 80.5,
    group: "financials",
    description: "View your financial summary, transactions, and export data",
  },
  {
    label: "Variations", // Renamed from "Variations" for client clarity
    path: "/variations",
    permissions: ["job:view"],
    priority: 80.1,
    group: "jobs", // Client: scope changes on their jobs
    description: "Manage job scope changes and variations",
  },
  {
    label: "Variations",
    path: "/variations",
    permissions: ["job:view", "financial:view"],
    priority: 80.12,
    group: "financials", // Client: variations in financials for reconciliation
    description: "Manage and reconcile job scope changes and variations",
  },
  {
    label: "My Variations",
    path: "/trade-variations",
    permissions: ["job:view", "financial:view"],
    priority: 80.25,
    group: "financials", // Trade: variation payouts
    description: "View your submitted variations and payouts",
  },
  {
    label: NAV_LABELS.CREDITS, // Renamed from "Client Ledger" for clarity
    path: "/billing/ledger",
    permissions: ["company:edit"],
    priority: 80.2,
    group: "financials", // Client: credits and billing
    description: "View credits and transaction history",
  },
  {
    label: "Invoices",
    path: "/billing/invoices",
    permissions: ["user:view", "financial:view", "company:edit"],
    priority: 80.15,
    group: "financials", // Client: invoices and billing
    description: "View and track your invoice history",
  },
  {
    label: "Reviews",
    path: "/dashboard/reviews",
    permissions: ["user:view"],
    priority: 80.05,
    group: "jobs", // Client: view reviews on their jobs
    description: "View reviews received from trades",
  },

  // Direct-to-Customer - trade manages own end customers
  {
    label: "My Customers",
    path: "/dashboard/direct/customers",
    permissions: ["quote:create"],
    priority: 76,
    group: "direct",
    description: "Manage your customer directory",
  },
  {
    label: "Direct Quotes",
    path: "/dashboard/direct/quotes",
    permissions: ["quote:create"],
    priority: 75,
    group: "direct",
    description: "Create and send quotes directly to customers",
  },
  {
    label: "Direct Jobs",
    path: "/dashboard/direct/jobs",
    permissions: ["job:view"],
    priority: 74,
    group: "direct",
    description: "Manage jobs from your direct customers",
  },
  {
    label: "Branding",
    path: "/dashboard/direct/branding",
    permissions: ["company:edit"],
    priority: 73,
    group: "direct",
    description: "Customize your logo, colors, and quote templates",
  },
  {
    label: "QR Codes",
    path: "/dashboard/qr-codes",
    permissions: ["quote:create", "job:view"],
    priority: 72.5,
    group: "direct",
    description: "Share your branded QR code and manage direct customer job requests",
  },

  // QR Codes - for client companies to generate job submission QR codes
  {
    label: "QR Codes",
    path: "/dashboard/qr-codes",
    permissions: ["job:create"],
    priority: 78.5,
    group: "jobs", // Moved inside Jobs dropdown
    description: "Manage QR codes for job submissions",
  },


  // Company Management - for Trade Company Owners/Admins
  {
    label: "Documents",
    path: "/documents",
    permissions: ["document:upload", "document:view"],
    priority: 88,
    group: "main",
    description: "Upload and manage compliance documents",
  },
  {
    label: "Team",
    path: "/settings/team",
    permissions: ["company:manage_users", "user:invite"],
    priority: 78,
    group: "settings",
    description: "Manage team members and permissions",
  },
  {
    label: "Job Settings",
    path: "/settings/company-jobs",
    permissions: ["company:edit"],
    priority: 77,
    group: "settings",
    description: "Configure job defaults and settings",
  },
  {
    label: "Integrations",
    path: "/settings/integrations",
    permissions: ["company:edit"],
    priority: 76.2,
    group: "settings",
    description: "Connect Xero, ServiceM8, and other services",
  },
  {
    label: "API & Webhooks",
    path: "/settings/api",
    permissions: ["company:edit"],
    priority: 76.1,
    group: "settings",
    description: "Manage Partner API keys, request logs, and webhook endpoints",
  },

  // Admin Section - all working pages with category grouping
  // Category: users - User and approval management
  {
    label: "Admin Dashboard",
    path: "/admin/dashboard",
    permissions: ["platform:view_all"],
    priority: 70,
    group: "admin",
    adminCategory: "users",
    description: "Platform administration overview",
  },
  {
    label: "Live Job Monitor",
    path: "/admin/job-monitor",
    permissions: ["platform:view_all"],
    priority: 69,
    group: "admin",
    adminCategory: "users",
    description: "Real-time job activity monitoring",
  },
  {
    label: "Service Cases",
    path: "/admin/service-cases",
    permissions: ["platform:view_all"],
    priority: 68.8,
    group: "admin",
    adminCategory: "users",
    description: "Product fault cases on jobs and registered installs: triage, RMA, coverage, hazards",
  },
  {
    label: "Job Matching",
    path: "/admin/job-matching",
    permissions: ["platform:view_all"],
    priority: 68.5,
    group: "admin",
    adminCategory: "users",
    description: "Ranked trade companies for each open job by distance, skills, accreditations and rating",
  },
  {
    label: "User Management",
    path: "/admin/users",
    permissions: ["platform:view_all", "crm:manage"],
    priority: 65,
    group: "admin",
    adminCategory: "users",
    description: "Manage platform users and permissions",
  },
  {
    label: "Customer Approvals",
    path: "/admin/approvals",
    permissions: ["platform:view_all", "crm:manage"],
    priority: 63,
    group: "admin",
    adminCategory: "users",
    description: "Approve or reject pending customer accounts",
  },
  {
    label: "CRM",
    path: "/admin/crm",
    permissions: ["platform:view_all", "crm:manage"],
    priority: 62.5,
    group: "admin",
    adminCategory: "users",
    description: "Manage client and trade leads, interactions, and follow-ups",
  },
  {
    label: "Document Review",
    path: "/admin/documents/review",
    permissions: ["platform:view_all", "crm:manage"],
    priority: 60,
    group: "admin",
    adminCategory: "users",
    description: "Review and approve trade documents",
  },
  // Category: content - Content and configuration management
  {
    label: "Trade Types",
    path: "/admin/trade-types",
    permissions: ["platform:view_all"],
    priority: 59,
    group: "admin",
    adminCategory: "content",
    description: "Manage trade types and aliases",
  },
  {
    label: "Certificate Rules",
    path: "/admin/certificate-rules",
    permissions: ["platform:view_all"],
    priority: 58.9,
    group: "admin",
    adminCategory: "content",
    description: "Manage compliance certificate deadlines and reminder rules per state and trade",
  },
  {
    label: "Service Types",
    path: "/admin/service-types",
    permissions: ["platform:view_all"],
    priority: 58.7,
    group: "admin",
    adminCategory: "content",
    description: "Manage service types, base prices, complexity levels, and task mappings",
  },
  {
    label: "Accreditations",
    path: "/admin/accreditations",
    permissions: ["platform:view_all"],
    priority: 58.5,
    group: "admin",
    adminCategory: "content",
    description: "Manage accreditations and their trade type dependencies",
  },
  {
    label: "Specializations",
    path: "/admin/specializations",
    permissions: ["platform:view_all"],
    priority: 58.3,
    group: "admin",
    adminCategory: "content",
    description: "Manage specializations for job matching",
  },
  {
    label: "Workflow Templates",
    path: "/admin/workflow-templates",
    permissions: ["platform:view_all"],
    priority: 58,
    group: "admin",
    adminCategory: "content",
    description: "Manage workflow templates for job processes",
  },
  {
    label: "Variation Detection Prompts",
    path: "/admin/variation-prompts",
    permissions: ["platform:view_all"],
    priority: 57.8,
    group: "admin",
    adminCategory: "content",
    description: "Manage the AI prompts that detect potential site variations in job photos",
  },
  {
    label: "Integration Templates",
    path: "/admin/integration-templates",
    permissions: ["platform:view_all"],
    priority: 57.5,
    group: "admin",
    adminCategory: "content",
    description: "Manage prebuilt integration templates for customers",
  },
  {
    label: "Expert Knowledge",
    path: "/admin/experts/documents",
    permissions: ["platform:view_all"],
    priority: 57.2,
    group: "admin",
    adminCategory: "content",
    description: "Manage expert knowledge base documents, ingestion pipeline, and test console",
  },
  {
    label: "Technical Expert",
    path: "/admin/experts/chat",
    permissions: ["platform:view_all"],
    priority: 57.18,
    group: "admin",
    adminCategory: "content",
    description: "Hold multi-turn conversations with the trade Technical Expert across manuals and standards",
  },
  {
    label: "Expert Flows",
    path: "/admin/experts/flows",
    permissions: ["platform:view_all"],
    priority: 57.15,
    group: "admin",
    adminCategory: "content",
    description: "Draft, edit, validate, preview, and publish troubleshooting decision trees from manuals",
  },
  {
    label: "Expert Manufacturers",
    path: "/admin/experts/manufacturers",
    permissions: ["platform:view_all"],
    priority: 57.1,
    group: "admin",
    adminCategory: "content",
    description: "Manage manufacturers, tech support lines, product series/models, and AnythingLLM workspaces",
  },
  {
    label: "Expert Transcripts",
    path: "/admin/experts/transcripts",
    permissions: ["platform:view_all"],
    priority: 57.08,
    group: "admin",
    adminCategory: "content",
    description: "Browse expert chat sessions, filter risky answers, and review transcripts",
  },
  {
    label: "AI Conversations",
    path: "/admin/ai-conversations",
    permissions: ["platform:view_all"],
    priority: 57.07,
    group: "admin",
    adminCategory: "content",
    description: "Review help chat questions and answers, expert chat volume, and warranty AI classifications",
  },
  {
    label: "Expert Metrics",
    path: "/admin/experts/metrics",
    permissions: ["platform:view_all"],
    priority: 57.06,
    group: "admin",
    adminCategory: "content",
    description: "View expert chat sessions, refusal rates, feedback, and per-manufacturer metrics",
  },
  {
    label: "Expert Pilot",
    path: "/admin/experts/pilot",
    permissions: ["platform:view_all"],
    priority: 57.04,
    group: "admin",
    adminCategory: "content",
    description: "Control trade pilot rollout, cohort companies, and standards retrieval flags",
  },
  {
    label: "Partner QR",
    path: "/admin/experts/partner-qr",
    permissions: ["platform:view_all"],
    priority: 57.03,
    group: "admin",
    adminCategory: "content",
    description: "Partner QR service bot: switch, demo settings, partner codes and their QR, and scans to requests per code",
  },
  {
    label: "Expert Test Sets",
    path: "/admin/experts/test-sets",
    permissions: ["platform:view_all"],
    priority: 57.05,
    group: "admin",
    adminCategory: "content",
    description: "Manage go-live verification test sets, AI-drafted questions, and scoring runs for the expert assistant",
  },
  {
    label: "Field Tips",
    path: "/admin/experts/tips",
    permissions: ["platform:view_all"],
    priority: 57.05,
    group: "admin",
    adminCategory: "content",
    description: "Review trade field tips for knowledge ingestion and inspect the installed assets register",
  },
  {
    label: "Help Center",
    path: "/admin/knowledge-base",
    permissions: ["platform:view_all"],
    priority: 57,
    group: "admin",
    adminCategory: "content",
    description: "Manage knowledge base articles and categories",
  },
  {
    label: "Industry News",
    path: "/admin/news",
    permissions: ["platform:view_all"],
    priority: 56.5,
    group: "admin",
    adminCategory: "content",
    description: "Manage public industry news articles and categories",
  },
  {
    label: "Communication Templates",
    path: "/admin/communication-templates",
    permissions: ["platform:view_all"],
    priority: 42,
    group: "admin",
    adminCategory: "content",
    description: "Manage email, SMS, push, and in-app notification templates",
  },
  {
    label: "Campaigns",
    path: "/admin/campaigns",
    permissions: ["platform:view_all", "crm:manage"],
    priority: 41.8,
    group: "admin",
    adminCategory: "content",
    description: "Write, preview, test, send and track campaigns",
  },
  {
    label: "Communication Logs",
    path: "/admin/communication-logs",
    permissions: ["platform:view_all"],
    priority: 41.5,
    group: "admin",
    adminCategory: "system",
    description: "View history of sent emails and SMS messages",
  },
  {
    label: "SMS Conversations",
    path: "/admin/sms-conversations",
    permissions: ["platform:view_all"],
    priority: 41.4,
    group: "admin",
    adminCategory: "system",
    description: "Audit two-way SMS threads and triage unmatched inbound messages",
  },
  {
    label: "Pricing Configuration",
    path: "/admin/pricing",
    permissions: ["platform:view_all"],
    priority: 45,
    group: "admin",
    adminCategory: "content",
    description: "Manage pricing modifiers and scenarios",
  },
  {
    label: "Platform Documents",
    path: "/admin/platform-documents",
    permissions: ["platform:view_all"],
    priority: 41.3,
    group: "admin",
    adminCategory: "content",
    description: "Manage Terms & Conditions and other platform documents",
  },
  // Category: compliance - Document and compliance management
  {
    label: "Company Compliance",
    path: "/admin/companies",
    permissions: ["platform:view_all", "crm:manage"],
    priority: 57,
    group: "admin",
    adminCategory: "compliance",
    description: "Audit company team members and granular licence compliance",
  },
  {
    label: "Compliance",
    path: "/admin/compliance",
    permissions: ["platform:view_all"],
    priority: 41,
    group: "admin",
    adminCategory: "compliance",
    description: "Monitor trade company compliance and document status",
  },
  {
    label: "Allocated Compliance",
    path: "/admin/compliance/allocated",
    permissions: ["platform:view_all"],
    priority: 40.5,
    group: "admin",
    adminCategory: "compliance",
    description: "Audit document and payout compliance for trade companies assigned to jobs",
  },
  {
    label: "Document Types",
    path: "/admin/document-types",
    permissions: ["platform:view_all"],
    priority: 41,
    group: "admin",
    adminCategory: "compliance",
    description: "Configure document types and AI extraction fields",
  },
  {
    label: "Document Requirements",
    path: "/admin/document-requirements",
    permissions: ["platform:view_all"],
    priority: 58,
    group: "admin",
    adminCategory: "compliance",
    description: "Configure document requirements by trade type",
  },
  {
    label: "Verification Dashboard",
    path: "/admin/verification-dashboard",
    permissions: ["platform:view_all"],
    priority: 55,
    group: "admin",
    adminCategory: "compliance",
    description: "Monitor document verification workflow and metrics",
  },
  {
    label: "Rejection Templates",
    path: "/admin/rejection-templates",
    permissions: ["platform:view_all"],
    priority: 50,
    group: "admin",
    adminCategory: "compliance",
    description: "Manage document rejection templates",
  },
  {
    label: "NSW Licence Lookup (Test)",
    path: "/admin/nsw-licence-lookup",
    permissions: ["platform:view_all"],
    priority: 49,
    group: "admin",
    adminCategory: "compliance",
    description: "Search and verify NSW Fair Trading trade licences",
  },
  // Category: finance - Financial and dispute management
  {
    label: "Financial Dashboard",
    path: "/admin/financial",
    permissions: ["platform:view_all"],
    priority: 42,
    group: "admin",
    adminCategory: "finance",
    description: "Platform financials, pending payouts, and dispute resolution",
  },
  {
    label: "Cash Flow Forecast",
    path: "/admin/job-projections",
    permissions: ["platform:view_all"],
    priority: 41.7,
    group: "admin",
    adminCategory: "finance",
    description: "Forward pipeline cash flow projections and uncompleted job revenue estimates",
  },
  {
    label: "Referrals & Payouts",
    path: "/admin/referrals",
    permissions: ["platform:view_all"],
    priority: 41.6,
    group: "admin",
    adminCategory: "finance",
    description: "Manage referral campaigns, QR stickers, and settle payouts",
  },
  {
    label: "Balance Top-Up",
    path: "/admin/balance-topup",
    permissions: ["platform:view_all"],
    priority: 41.55,
    group: "admin",
    adminCategory: "finance",
    description: "Top up the platform Stripe balance via Checkout",
  },
  {
    label: "Invoices",
    path: "/admin/invoices",
    permissions: ["platform:view_all"],
    priority: 41.52,
    group: "admin",
    adminCategory: "finance",
    description: "View and manage all invoices on the platform",
  },
  {
    label: "Remittance History",
    path: "/admin/remittances",
    permissions: ["platform:view_all"],
    priority: 41.515,
    group: "admin",
    adminCategory: "finance",
    description: "View, search, and download Remittance Advice across all trade companies",
  },
  {
    label: "Credits",
    path: "/admin/credits",
    permissions: ["platform:view_all"],
    priority: 41.51,
    group: "admin",
    adminCategory: "finance",
    description: "View and manage client credits and ledger entries",
  },
  {
    label: "Disputes",
    path: "/admin/disputes",
    permissions: ["platform:view_all"],
    priority: 41.5,
    group: "admin",
    adminCategory: "finance",
    description: "Manage customer disputes and refunds",
  },
  {
    label: "All Variations",
    path: "/admin/variations",
    permissions: ["platform:view_all"],
    priority: 41.47,
    group: "admin",
    adminCategory: "finance",
    description: "View and track all variations across the platform",
  },
  {
    label: "Variation Disputes",
    path: "/admin/variation-disputes",
    permissions: ["platform:view_all"],
    priority: 41.45,
    group: "admin",
    adminCategory: "finance",
    description: "Resolve variation disputes between clients and trades",
  },
  {
    label: "Cancellation Penalties",
    path: "/admin/cancellation-penalties",
    permissions: ["platform:view_all"],
    priority: 41.43,
    group: "admin",
    adminCategory: "finance",
    description: "Review, confirm, and waive trade payout deductions from cancellations",
  },
  {
    label: "Reviews",
    path: "/admin/reviews",
    permissions: ["platform:view_all"],
    priority: 41.4,
    group: "admin",
    adminCategory: "finance",
    description: "Moderate job reviews and feedback",
  },
  {
    label: "Trade Analytics",
    path: "/admin/trade-analytics",
    permissions: ["platform:view_all"],
    priority: 41.35,
    group: "admin",
    adminCategory: "finance",
    description: "Platform-wide trade company performance analytics",
  },
  {
    label: "App Rollout",
    path: "/admin/app-rollout",
    permissions: ["platform:view_all"],
    priority: 41.34,
    group: "admin",
    adminCategory: "finance",
    description: "Mobile app rollout, adoption and reachability metrics",
  },
  {
    label: "Coverage Map",
    path: "/admin/coverage-map",
    permissions: ["platform:view_all"],
    priority: 41.33,
    group: "admin",
    adminCategory: "users",
    description: "Interactive map of trade company locations and service area coverage",
  },
  {
    label: "Direct Job Settings",
    path: "/admin/direct-job-settings",
    permissions: ["platform:view_all"],
    priority: 41.2,
    group: "admin",
    adminCategory: "finance",
    description: "Manage trade direct-to-customer fees and access",
  },
  {
    label: "Payment terms",
    path: "/admin/payment-terms",
    permissions: ["platform:view_all"],
    priority: 41.1,
    group: "admin",
    adminCategory: "finance",
    description: "Manage client payment terms, default credit limits, and fee rates",
  },
  // Category: system - System health and monitoring
  {
    label: "Scheduling SLA Hub",
    path: "/admin/scheduling-sla",
    permissions: ["platform:view_all"],
    priority: 40.5,
    group: "admin",
    adminCategory: "system",
    description: "Platform scheduling SLA countdowns, forfeiture controls, and customer voice dials",
  },
  {
    label: "System Health",
    path: "/admin/health",
    permissions: ["platform:view_all"],
    priority: 40,
    group: "admin",
    adminCategory: "system",
    description: "Monitor system performance and health",
  },
  {
    label: "Notification Log",
    path: "/admin/notification-log",
    permissions: ["platform:view_all"],
    priority: 39.5,
    group: "admin",
    adminCategory: "system",
    description: "Audit log of all platform notifications",
  },
  {
    label: "Audit Logs",
    path: "/admin/audit",
    permissions: ["platform:view_all"],
    priority: 35,
    group: "admin",
    adminCategory: "system",
    description: "View system audit logs and security events",
  },
  {
    label: "Issue Center",
    path: "/admin/issues",
    permissions: ["platform:view_all"],
    priority: 34,
    group: "admin",
    adminCategory: "system",
    description: "Manage user-reported feedback and system issues",
  },
  {
    label: "AI Analyst",
    path: "/admin/ai-analyst",
    permissions: ["platform:view_all"],
    priority: 33.5,
    group: "admin",
    adminCategory: "system",
    description: "AI-powered read-only database analyst for natural-language queries",
  },
  {
    label: "Platform Staff",
    path: "/admin/platform-staff",
    permissions: ["platform:view_all"],
    priority: 33,
    group: "admin",
    adminCategory: "system",
    description: "Manage platform administrators, support staff, and viewers",
  },
  {
    label: "Roles & Permissions",
    path: "/admin/roles",
    permissions: ["platform:view_all"],
    priority: 32,
    group: "admin",
    adminCategory: "system",
    description: "Configure RBAC roles and permission assignments",
  },
  // Warehouse & Inventory (Job & Stock Flow) — platform-admin tooling.
  // Grouped under a dedicated "logistics" adminCategory so the whole inventory
  // surface is discoverable in one place instead of scattered/hidden.
  {
    label: "Warehouses & Locations",
    path: "/admin/locations",
    permissions: ["platform:view_all"],
    priority: 41.45,
    group: "admin",
    adminCategory: "logistics",
    description: "Manage supplier and warehouse pickup locations for material logistics",
  },
  {
    label: "Products & Stock",
    path: "/admin/products",
    permissions: ["platform:view_all"],
    priority: 41.44,
    group: "admin",
    adminCategory: "logistics",
    description: "Manage the product catalogue, SKUs, and per-warehouse stock levels",
  },
  {
    label: "Warehouse Staging",
    path: "/admin/staging",
    permissions: ["platform:view_all"],
    priority: 41.43,
    group: "admin",
    adminCategory: "logistics",
    description: "Pick & pack auto-generated staging tickets for scheduled jobs",
  },
  {
    label: "Fleet / Vehicles",
    path: "/admin/vehicles",
    permissions: ["platform:view_all"],
    priority: 41.42,
    group: "admin",
    adminCategory: "logistics",
    description: "View registered trade vehicles across companies (Chain-of-Responsibility)",
  },
  {
    label: "My Vehicles",
    path: "/dashboard/vehicles",
    permissions: ["job:view"],
    priority: 80.7,
    group: "settings",
    description: "Register your vehicles for Chain-of-Responsibility payload checks",
  },
];

// Get navigation links for a user based on their permissions with smart deduplication
export const getNavigationForUser = (
  permissions: string[],
  _companyType?: string,
  directJobsEnabled?: boolean,
): Array<{ label: string; path: string; group?: string }> => {
  if (!permissions || permissions.length === 0) {
    return [];
  }

  // Filter navigation items based on permissions
  let allowedLinks = navigationItems.filter((item) =>
    item.permissions.some((permission) => permissions?.includes(permission))
  );

  // Filter out certain nav items for customers (Documents and My Rates are now allowed)
  if (_companyType === "customer") {
    allowedLinks = allowedLinks.filter((item) =>
      item.label !== "Payments"
    );
  }

  // F6 B / G24 (client app PR1): a client company's invoices, credits, financial dashboard and
  // the financials group follow client:financial_docs (Owners and Admins), not user:view or
  // company:edit, so a Coordinator sees none of them.
  if (_companyType === "customer" && !permissions.includes(CLIENT_CAPABILITIES.financialDocs)) {
    allowedLinks = allowedLinks.filter((item) =>
      item.group !== "financials" && !CLIENT_FINANCIAL_PATHS.has(item.path)
    );
  }

  // Filter out Job Settings, Scope Changes (client page), and Reviews nav for trade companies
  // Trade users should see "My Variations" instead and reviews in dashboard sidebar
  if (_companyType === "trade_company") {
    allowedLinks = allowedLinks.filter((item) =>
      item.label !== "Job Settings" &&
      item.label !== "Variations" && // Renamed from "Variations"
      item.label !== "Credits" && // Renamed from "Client Ledger"
      item.path !== "/dashboard/delivery-pipeline" && // Use path to avoid filtering the renamed Lead Pipeline
      item.label !== "Workflows" &&
      item.label !== "API & Webhooks" &&
      item.path !== "/dashboard/reviews"
    );

    // Filter out Direct-to-Customer items if not enabled for this trade company
    if (!directJobsEnabled) {
      allowedLinks = allowedLinks.filter((item) => item.group !== "direct");
    }
  }

  // Filter out My Variations for client companies - they have their own Scope Changes page
  if (_companyType === "customer") {
    allowedLinks = allowedLinks.filter((item) =>
      item.label !== "My Variations" &&
      item.path !== "/dashboard/lead-pipeline" && // Use path to filter the trade-specific pipeline
      item.label !== "My Reviews" &&
      item.label !== "Quote Templates" &&
      item.label !== "Staff Schedule" &&
      item.label !== "Staff Performance" &&
      item.label !== "Analytics" &&
      item.label !== "Service Area" && // Service area is trade-only (powers job matching)
      item.label !== "My Vehicles" && // Vehicle registration is trade-only (CoR payload checks)
      item.group !== "direct" // Direct-to-Customer is trade-only
    );
  }

  // Filter out client-only features for trade companies
  if (_companyType === "trade_company") {
    allowedLinks = allowedLinks.filter((item) =>
      !(item.label === "QR Codes" && item.group !== "direct") &&
      item.label !== "Invoices" &&
      item.label !== "Credits"
    );
  }

  // Deduplicate by group and path, keeping highest priority item per group
  const deduplicatedLinks = new Map<string, NavLink>();

  allowedLinks.forEach((link) => {
    const key = `${link.group || 'other'}:${link.path}`;
    const existing = deduplicatedLinks.get(key);
    if (
      !existing ||
      (link.priority && existing.priority && link.priority > existing.priority)
    ) {
      deduplicatedLinks.set(key, link);
    }
  });

  // Sort by priority (highest first) and return simplified format
  return Array.from(deduplicatedLinks.values())
    .sort((a, b) => (b.priority || 0) - (a.priority || 0))
    .map(({ label, path, group }) => ({ label, path, group }));
};

// Get navigation items grouped by category
export const getGroupedNavigationForUser = (
  permissions: string[],
  companyType?: string,
  directJobsEnabled?: boolean,
): Record<string, Array<{ label: string; path: string }>> => {
  const userLinks = getNavigationForUser(
    permissions,
    companyType,
    directJobsEnabled,
  );

  return userLinks.reduce((groups, link) => {
    const group = link.group || "other";
    if (!groups[group]) {
      groups[group] = [];
    }
    groups[group].push({ label: link.label, path: link.path });
    return groups;
  }, {} as Record<string, Array<{ label: string; path: string }>>);
};

// Admin category display labels
export const adminCategoryLabels: Record<string, string> = {
  users: "Users & Approvals",
  content: "Content & Config",
  logistics: "Warehouse & Inventory",
  compliance: "Compliance & Documents",
  finance: "Finance & Disputes",
  system: "System",
};

// Get admin navigation items grouped by adminCategory for nested menu display
export const getAdminNavigationByCategory = (
  permissions: string[],
): Record<string, Array<{ label: string; path: string }>> => {
  // Filter to admin items only
  const adminLinks = navigationItems
    .filter((item) =>
      item.group === "admin" &&
      item.permissions.some((permission) => permissions?.includes(permission))
    )
    .sort((a, b) => (b.priority || 0) - (a.priority || 0));

  // Group by adminCategory
  return adminLinks.reduce((groups, link) => {
    const category = link.adminCategory || "other";
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push({ label: link.label, path: link.path });
    return groups;
  }, {} as Record<string, Array<{ label: string; path: string }>>);
};


// Extra words people search the admin menu by, where the page name doesn't say it (path -> words).
export const adminSearchKeywords: Record<string, string[]> = {
  "/admin/communication-logs": ["sms", "texts", "emails", "sent", "delivery"],
  "/admin/sms-conversations": ["sms", "texts", "replies", "inbox"],
  "/admin/notification-log": ["push", "notifications", "bell"],
  "/admin/communication-templates": ["comms", "wording", "email", "sms", "push", "templates"],
  "/admin/campaigns": ["marketing", "broadcast", "promotions", "bulk sms"],
  "/admin/financial": ["money", "revenue", "payments"],
  "/admin/remittances": ["payouts", "paid", "csv"],
  "/admin/referrals": ["payouts"],
  "/admin/invoices": ["billing"],
  "/admin/credits": ["refunds"],
  "/admin/disputes": ["complaints"],
  "/admin/variations": ["extra cost"],
  "/admin/users": ["people", "accounts", "logins"],
  "/admin/approvals": ["sign up", "onboarding", "clients"],
  "/admin/companies": ["trades", "licences", "insurance"],
  "/admin/verification-dashboard": ["verify", "abn", "licences"],
  "/admin/issues": ["bugs", "kanban", "tickets", "cards"],
  "/admin/health": ["errors", "status", "uptime"],
  "/admin/audit": ["history", "changes", "who changed"],
  "/admin/roles": ["permissions", "access"],
  "/admin/platform-staff": ["staff", "team"],
  "/admin/app-rollout": ["app", "android", "iphone", "analytics", "adoption"],
  "/admin/trade-analytics": ["analytics", "stats"],
  "/admin/scheduling-sla": ["sla", "schedule", "robocall", "reminders"],
  "/admin/workflow-templates": ["checklists", "workflows", "steps"],
  "/admin/knowledge-base": ["help", "articles", "faq"],
  "/admin/experts/manufacturers": ["experts", "manufacturers", "brands", "series", "models", "anythingllm", "workspaces", "rma"],
  "/admin/experts/documents": ["experts", "knowledge", "manuals", "standards", "datasheets", "ingest", "rag", "test console"],
  "/admin/experts/chat": ["expert", "chat", "ask", "technical", "standards", "troubleshoot"],
  "/admin/experts/transcripts": ["experts", "transcripts", "chat", "sessions", "messages", "refusals", "safety", "moderation", "review"],
  "/admin/ai-conversations": ["ai", "chat", "conversations", "help chat", "bot", "transcripts", "warranty"],
  "/admin/experts/metrics": ["experts", "metrics", "analytics", "stats", "refusal rate", "thumbs", "kpi"],
  "/admin/experts/pilot": ["experts", "pilot", "cohort", "feature flags", "rollout", "standards"],
  "/admin/experts/partner-qr": ["experts", "partner qr", "service bot", "qr", "partner codes", "conference", "demo", "scans"],
  "/admin/experts/test-sets": ["experts", "test sets", "go live", "gate", "scoring", "evaluation", "qa", "benchmark"],
  "/admin/experts/flows": ["experts", "flows", "troubleshooting", "decision tree", "faults", "error codes", "manuals", "preview"],
  "/admin/experts/tips": ["experts", "field tips", "tips", "review", "installed assets", "assets", "serial", "equipment"],
  "/admin/pricing": ["margin", "commission", "price"],
  "/admin/payment-terms": ["terms", "deposit"],
  "/admin/job-monitor": ["jobs", "live"],
  "/admin/service-cases": ["service", "cases", "rma", "hazard", "warranty", "manufacturer", "registration", "coverage"],
  "/admin/job-matching": ["allocate", "assign"],
  "/admin/coverage-map": ["map", "areas", "service area"],
  "/admin/locations": ["warehouse"],
  "/admin/products": ["stock", "inventory", "parts"],
  "/admin/vehicles": ["fleet", "vans", "trucks"],
  "/admin/direct-job-settings": ["robocall", "direct"],
  "/admin/ai-analyst": ["ai", "sql", "questions"],
  "/admin/crm": ["contacts", "leads"],
  "/admin/documents/review": ["documents", "uploads"],
};

export interface AdminNavMatch {
  label: string;
  path: string;
  description?: string;
  category: string;
  categoryLabel: string;
}

const normalizeForSearch = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

const adminLinksFor = (permissions: string[]) =>
  navigationItems.filter((item) =>
    item.group === "admin" &&
    item.permissions.some((permission) => permissions?.includes(permission))
  );

const toAdminNavMatch = (item: NavLink): AdminNavMatch => {
  const category = item.adminCategory || "other";
  return {
    label: item.label,
    path: item.path,
    description: item.description,
    category,
    categoryLabel: adminCategoryLabels[category] || category,
  };
};

/** Every admin page the user can open, in menu priority order. */
export const listAdminNavigation = (permissions: string[]): AdminNavMatch[] =>
  adminLinksFor(permissions)
    .sort((a, b) => (b.priority || 0) - (a.priority || 0))
    .map(toAdminNavMatch);

/**
 * Admin pages the user can open that match a typed query, best first. Every word typed must appear in the page's
 * name, description, group name or search words; a page whose name starts with the query ranks first, then a name
 * word that starts with it, then other name matches, then search words, then description or group.
 */
export const searchAdminNavigation = (
  permissions: string[],
  query: string,
  limit = 12,
): AdminNavMatch[] => {
  const q = normalizeForSearch(query);
  if (!q) return [];
  const words = q.split(" ");

  return adminLinksFor(permissions)
    .map((item) => {
      const match = toAdminNavMatch(item);
      const label = normalizeForSearch(item.label);
      const keywords = normalizeForSearch((adminSearchKeywords[item.path] || []).join(" "));
      const other = normalizeForSearch(`${item.description || ""} ${match.categoryLabel}`);
      const haystack = `${label} ${keywords} ${other}`;
      if (!words.every((w) => haystack.includes(w))) return null;

      let score = 10;
      if (label.startsWith(q)) score = 100;
      else if (label.split(" ").some((w) => w.startsWith(words[0]))) score = 60;
      else if (label.includes(q)) score = 40;
      else if (keywords.split(" ").some((w) => w.startsWith(words[0]))) score = 30;

      return { score, match };
    })
    .filter((x): x is { score: number; match: AdminNavMatch } => x !== null)
    .sort((a, b) => b.score - a.score || a.match.label.localeCompare(b.match.label))
    .slice(0, limit)
    .map((x) => x.match);
};
